// Full bilingual (FR/EN) lexicon: statistical validation + proprietary signal terminology.
// Proprietary acronyms (ZII, ZRI, CSD...) are kept in French in both languages,
// matching what the app displays on charts and signals.

export const FAMILIES = [
  { key: "validation", fr: "Validation statistique", en: "Statistical validation" },
  { key: "zones", fr: "Zones d'intérêt", en: "Zones of interest" },
  { key: "structure", fr: "Structure de marché", en: "Market structure" },
  { key: "timing", fr: "Fenêtre temporelle & momentum", en: "Time windows & momentum" },
  { key: "regime", fr: "Régime de marché", en: "Market regime" },
  { key: "forecast", fr: "Prévision probabiliste", en: "Probabilistic forecasting" },
];

export const LEXICON_TERMS = [
  // ============ VALIDATION STATISTIQUE ============
  {
    key: "OVERFIT",
    acronym: "Overfitting",
    family: "validation",
    name: { fr: "Surapprentissage", en: "Overfitting (curve-fitting)" },
    easy: {
      fr: "Quand une stratégie est réglée si finement sur des données passées qu'elle « colle » parfaitement à l'historique testé — mais échoue en réel, parce qu'elle a appris le bruit et les coïncidences de cette période précise, pas une vraie logique de marché. Image simple : un élève qui a appris par cœur les réponses d'un examen passé, sans comprendre la matière — 20/20 sur cet examen-là, échec sur un nouvel examen.",
      en: "A strategy tuned so tightly to past data that it fits the tested history perfectly — then fails live, because it learned the noise and coincidences of that specific period rather than a real market logic. Think of a student who memorized the answers to last year's exam without understanding the subject: perfect score on that exam, failure on a new one.",
    },
    tech: {
      fr: "Erreur de généralisation : la performance in-sample (sur les données d'optimisation) surestime la performance out-of-sample (sur données nouvelles). Plus le nombre de paramètres ou d'essais augmente, plus le risque croît.",
      en: "Generalization error: in-sample performance overstates out-of-sample performance. Risk grows with the number of free parameters and the number of trials run during optimization.",
    },
    interpretation: {
      fr: "Un backtest brillant ne prouve rien tant qu'on n'a pas mesuré le risque d'overfitting. C'est le problème de fond que tous les tests de cette section cherchent à détecter.",
      en: "A great-looking backtest proves nothing until the overfitting risk has been quantified. It is the core problem every test in this section is designed to detect.",
    },
    usage: {
      fr: "Avant de risquer du capital réel, passez la stratégie par le PBO, le DSR et le test de permutation pour vérifier que l'edge n'est pas un artefact d'optimisation.",
      en: "Before risking real capital, run the strategy through PBO, DSR and the permutation test to check the edge is not an optimization artifact.",
    },
    oneLiner: {
      fr: "Le risque de fond : une stratégie qui a appris le passé par cœur au lieu d'une vraie logique",
      en: "The core risk: a strategy that memorized the past instead of learning a real edge",
    },
  },
  {
    key: "BACKTEST",
    acronym: "Backtest",
    family: "validation",
    name: { fr: "Backtest", en: "Backtest" },
    easy: {
      fr: "Le test d'une stratégie de trading sur des données historiques, pour voir quelle performance elle aurait eue dans le passé.",
      en: "Running a trading strategy over historical data to see how it would have performed in the past.",
    },
    tech: {
      fr: "Simulation de l'exécution des règles de la stratégie bougie par bougie sur l'historique, en incluant idéalement coûts de transaction, slippage et contraintes de capital.",
      en: "Bar-by-bar simulation of the strategy rules over historical data, ideally accounting for transaction costs, slippage and capital constraints.",
    },
    interpretation: {
      fr: "Un backtest positif est une condition nécessaire mais jamais suffisante : il ne dit rien, seul, sur la probabilité que la performance tienne en réel.",
      en: "A positive backtest is necessary but never sufficient: on its own it says nothing about whether the performance will hold live.",
    },
    usage: {
      fr: "Point de départ de toute analyse — c'est le matériau brut sur lequel les tests de robustesse (PBO, DSR, permutation) sont ensuite exécutés.",
      en: "The starting point of any analysis — the raw material on which the robustness tests (PBO, DSR, permutation) are then run.",
    },
    oneLiner: {
      fr: "Le test historique d'une stratégie — le matériau brut à valider",
      en: "The historical test of a strategy — the raw material to be validated",
    },
  },
  {
    key: "SHARPE",
    acronym: "Sharpe",
    family: "validation",
    name: { fr: "Ratio de Sharpe", en: "Sharpe Ratio" },
    easy: {
      fr: "Mesure le rendement d'une stratégie par rapport au risque pris. Plus il est élevé, mieux c'est en théorie — mais il peut être artificiellement gonflé (voir DSR).",
      en: "Measures a strategy's return relative to the risk taken. Higher is better in theory — but it can be artificially inflated (see DSR).",
    },
    tech: {
      fr: "SR = (rendement moyen − taux sans risque) / écart-type des rendements, généralement annualisé. Sensible à la non-normalité des rendements (skewness, kurtosis) et à la longueur de l'historique.",
      en: "SR = (mean return − risk-free rate) / standard deviation of returns, usually annualized. Sensitive to non-normal returns (skewness, kurtosis) and to track-record length.",
    },
    interpretation: {
      fr: "Un Sharpe élevé sur un historique court, ou obtenu après avoir testé beaucoup de variantes, ne veut pas dire grand-chose seul.",
      en: "A high Sharpe on a short history, or obtained after testing many variants, means little on its own.",
    },
    usage: {
      fr: "Ne jamais comparer des stratégies sur le Sharpe brut : utiliser le DSR, qui corrige les biais de sélection et de non-normalité.",
      en: "Never rank strategies on raw Sharpe: use the DSR, which corrects for selection bias and non-normality.",
    },
    oneLiner: {
      fr: "Rendement / risque — utile mais facilement gonflé, à corriger via le DSR",
      en: "Return per unit of risk — useful but easily inflated; correct it with the DSR",
    },
  },
  {
    key: "PBO",
    acronym: "PBO",
    family: "validation",
    name: { fr: "Probability of Backtest Overfitting (Probabilité de surapprentissage du backtest)", en: "Probability of Backtest Overfitting" },
    easy: {
      fr: "Estime la probabilité que la performance d'une stratégie backtestée soit due au hasard / à l'overfitting plutôt qu'à une vraie edge. Concrètement : on découpe l'historique en sous-périodes, on teste toutes les combinaisons possibles, et on regarde si la stratégie gagnante « en moyenne » reste bonne quand on change la combinaison de périodes. Si oui → probablement une vraie edge. Si la performance s'effondre selon la découpe → signe d'overfitting.",
      en: "Estimates the probability that a backtested strategy's performance is due to luck/overfitting rather than a real edge. The history is split into sub-periods, every possible combination is tested, and we check whether the strategy that wins 'on average' stays good when the combination of periods changes. If it does → likely a real edge. If performance collapses depending on the split → overfitting signal.",
    },
    tech: {
      fr: "Calculée par CSCV (Combinatorially Symmetric Cross-Validation, Bailey & López de Prado 2014) : l'historique est partitionné en S blocs, toutes les combinaisons S/2 in-sample vs S/2 out-of-sample sont évaluées, et le PBO est la fréquence à laquelle la stratégie la mieux classée in-sample tombe sous la médiane out-of-sample.",
      en: "Computed via CSCV (Combinatorially Symmetric Cross-Validation, Bailey & López de Prado 2014): the history is partitioned into S blocks, every S/2 in-sample vs S/2 out-of-sample combination is evaluated, and PBO is the frequency with which the best in-sample strategy ranks below the out-of-sample median.",
    },
    interpretation: {
      fr: "Un pourcentage — plus il est bas, mieux c'est. < 20 % : robuste, la performance ne dépend pas d'une découpe chanceuse. 20-50 % : zone grise, prudence. > 50 % : overfitting probable, l'edge apparent est vraisemblablement du bruit.",
      en: "A percentage — lower is better. < 20%: robust, performance doesn't depend on a lucky split. 20–50%: grey zone, be careful. > 50%: likely overfitting, the apparent edge is probably noise.",
    },
    usage: {
      fr: "Test central du Rapport d'Analyse Statistique : un PBO élevé signifie « ne pas augmenter le risque sur la base de ce backtest » — ajoutez de l'historique et re-testez.",
      en: "Core test of the Statistical Analysis Report: a high PBO means 'do not size up based on this backtest' — add more history and re-test.",
    },
    oneLiner: {
      fr: "Le classement de ma stratégie reste-t-il bon selon différentes découpes de temps ?",
      en: "Does my strategy's ranking survive different time splits?",
    },
  },
  {
    key: "DSR",
    acronym: "DSR",
    family: "validation",
    name: { fr: "Deflated Sharpe Ratio (Ratio de Sharpe « dégonflé »)", en: "Deflated Sharpe Ratio" },
    easy: {
      fr: "Si vous testez des centaines de variantes d'une stratégie et gardez la meilleure, son Sharpe est artificiellement gonflé par le simple fait d'avoir essayé beaucoup de combinaisons — comme gagner à la loterie en achetant énormément de billets, puis prétendre que le billet gagnant avait une « méthode ». Le DSR corrige ce biais : il ajuste le Sharpe à la baisse en tenant compte du nombre d'essais, pour une estimation honnête de la vraie performance attendue.",
      en: "If you test hundreds of strategy variants and keep the best one, its Sharpe is inflated by the sheer number of trials — like winning the lottery by buying a huge stack of tickets, then claiming your winning ticket had a 'method'. The DSR corrects this selection bias by deflating the Sharpe for the number of trials, giving an honest estimate of true expected performance.",
    },
    tech: {
      fr: "DSR = probabilité que le vrai Sharpe soit > 0, après correction du Sharpe observé pour la skewness, la kurtosis, la longueur de l'historique et le Sharpe maximal attendu sous H0 compte tenu du nombre d'essais N (Bailey & López de Prado 2014).",
      en: "DSR = probability that the true Sharpe is > 0, after adjusting the observed Sharpe for skewness, kurtosis, track-record length and the expected maximum Sharpe under H0 given N trials (Bailey & López de Prado 2014).",
    },
    interpretation: {
      fr: "DSR > 0,95 : le Sharpe observé est significatif à 95 %, difficilement attribuable à la chance. DSR < 0,95 (proche de 0 ou négatif) : la performance observée peut s'expliquer par le hasard des essais multiples.",
      en: "DSR > 0.95: the observed Sharpe is significant at the 95% level, hard to attribute to luck. DSR < 0.95 (near zero or negative): the observed performance can be explained by multiple-testing luck alone.",
    },
    usage: {
      fr: "Comparer et valider des stratégies sur le DSR plutôt que sur le Sharpe brut, surtout après une phase d'optimisation de paramètres.",
      en: "Rank and validate strategies on DSR rather than raw Sharpe, especially after a parameter-optimization phase.",
    },
    oneLiner: {
      fr: "Ma performance tient-elle compte du nombre de variantes que j'ai essayées ?",
      en: "Does my performance survive a correction for how many variants I tried?",
    },
  },
  {
    key: "PKF",
    acronym: "Purged K-Fold",
    family: "validation",
    name: { fr: "Validation croisée « purgée »", en: "Purged K-Fold Cross-Validation" },
    easy: {
      fr: "Une méthode qui découpe l'historique en plusieurs blocs (« folds »), entraîne/teste la stratégie sur différentes combinaisons de ces blocs, pour vérifier qu'elle marche de façon cohérente sur plusieurs périodes — pas seulement sur celle que vous avez choisi de regarder. « Purgé » : les données autour d'une frontière entre deux blocs peuvent se contaminer (ex. une position à cheval sur deux périodes) ; la méthode retire ces zones de chevauchement pour ne pas fausser le résultat.",
      en: "Splits the history into several blocks ('folds'), then trains/tests the strategy on different combinations of those blocks, checking it works consistently across multiple periods — not just the one you happened to look at. 'Purged': data near a fold boundary can leak into each other (e.g. a position straddling two periods); those overlapping observations are removed to avoid contaminating the result.",
    },
    tech: {
      fr: "Validation croisée K-fold adaptée aux séries temporelles financières (López de Prado 2018) : purge des observations dont les labels chevauchent la frontière train/test, plus un embargo (délai supplémentaire) après chaque bloc de test pour éliminer la fuite d'information résiduelle.",
      en: "K-fold cross-validation adapted to financial time series (López de Prado 2018): observations whose labels overlap the train/test boundary are purged, and an embargo (extra gap) is added after each test block to remove residual information leakage.",
    },
    interpretation: {
      fr: "Si la performance s'effondre en Purged K-Fold par rapport au backtest simple, c'est un signal d'overfitting : la stratégie ne fonctionnait « que par chance » sur une tranche de temps.",
      en: "If performance collapses under Purged K-Fold versus the plain backtest, that's an overfitting signal: the strategy only worked 'by luck' on one slice of time.",
    },
    usage: {
      fr: "Utilisée dans le ML Lab pour obtenir une estimation honnête de la performance hors-échantillon d'un modèle avant toute décision.",
      en: "Used in the ML Lab to get an honest out-of-sample performance estimate for a model before any decision.",
    },
    oneLiner: {
      fr: "Ma stratégie marche-t-elle sur plusieurs périodes, sans contamination entre elles ?",
      en: "Does my strategy work across multiple periods, with no leakage between them?",
    },
  },
  {
    key: "PERM",
    acronym: "Permutation",
    family: "validation",
    name: { fr: "Test de permutation (sign-flip)", en: "Permutation test (sign-flip)" },
    easy: {
      fr: "On mélange aléatoirement les données (ou le signe des trades) des milliers de fois, pour voir si la performance de la stratégie reste supérieure à ce qu'on obtiendrait par pur hasard. Image simple : si vous mélangez un jeu de cartes 1 000 fois et qu'à chaque fois votre « stratégie » gagne quand même, c'est bon signe. Si elle ne gagne pas plus souvent qu'un tirage aléatoire, elle n'apporte rien de réel.",
      en: "The data (or the sign of each trade) is randomly shuffled thousands of times to see whether the strategy still beats what pure chance would produce. If you shuffle a deck 1,000 times and your 'strategy' still wins every time, that's a good sign. If it doesn't win more often than a random draw, it adds nothing real.",
    },
    tech: {
      fr: "Test non-paramétrique : sous H0 (« aucune edge »), le signe de chaque trade est interchangeable. On génère des milliers de permutations sign-flip des P&L, et la p-value est la proportion de permutations qui font au moins aussi bien que la stratégie réelle.",
      en: "Non-parametric test: under H0 ('no edge'), each trade's sign is exchangeable. Thousands of sign-flip permutations of the P&L are generated; the p-value is the share of permutations that do at least as well as the real strategy.",
    },
    interpretation: {
      fr: "p < 0,05 : moins de 5 % de chances que le hasard produise ce résultat — edge significatif. p ≥ 0,05 : impossible d'exclure le hasard.",
      en: "p < 0.05: less than a 5% chance that randomness produces this result — significant edge. p ≥ 0.05: randomness cannot be ruled out.",
    },
    usage: {
      fr: "Exécuté dans le Rapport d'Analyse Statistique sur VOS trades importés : ne pas augmenter le risque si le test ne rejette pas le hasard.",
      en: "Run in the Statistical Analysis Report on YOUR imported trades: do not size up if the test cannot reject randomness.",
    },
    oneLiner: {
      fr: "Est-ce que je fais mieux que le pur hasard ?",
      en: "Do I actually beat pure chance?",
    },
  },
  {
    key: "PVALUE",
    acronym: "p-value",
    family: "validation",
    name: { fr: "p-value (valeur-p)", en: "p-value" },
    easy: {
      fr: "Mesure la probabilité d'observer un résultat aussi bon (ou meilleur) que le vôtre si en réalité votre stratégie n'avait aucune edge (pur hasard). Plus la p-value est basse, plus c'est rassurant.",
      en: "The probability of observing a result as good as (or better than) yours if your strategy actually had no edge (pure chance). The lower the p-value, the more reassuring.",
    },
    tech: {
      fr: "P(résultat ≥ observé | H0 vraie), où H0 = « aucune edge ». Dans l'outil, elle est estimée empiriquement via le test de permutation sign-flip, sans hypothèse de normalité.",
      en: "P(result ≥ observed | H0 true), where H0 = 'no edge'. In the tool it is estimated empirically via the sign-flip permutation test, with no normality assumption.",
    },
    interpretation: {
      fr: "p < 0,05 = edge statistiquement significatif au seuil de 5 %. Une p-value élevée (> 5 %) est un signal d'alerte : la performance peut être due au hasard.",
      en: "p < 0.05 = statistically significant edge at the 5% level. A high p-value (> 5%) is a warning: the performance may be luck.",
    },
    usage: {
      fr: "Lire la p-value du rapport avant toute décision de sizing : elle quantifie le risque que votre historique de trades soit un coup de chance.",
      en: "Read the report's p-value before any sizing decision: it quantifies the risk that your trade history is a fluke.",
    },
    oneLiner: {
      fr: "Quelle est la probabilité que mon résultat soit dû au hasard ?",
      en: "How likely is it that my result is just luck?",
    },
  },

  // ============ ZONES D'INTÉRÊT ============
  {
    key: "ZII",
    acronym: "ZII",
    family: "zones",
    name: { fr: "Zone d'Intervention Institutionnelle", en: "Institutional Intervention Zone (French acronym kept as in the app)" },
    easy: {
      fr: "Une zone du graphique où de très gros acheteurs ou vendeurs sont récemment intervenus, laissant une empreinte forte. Zone probable de retour du prix.",
      en: "A chart area where very large buyers or sellers recently stepped in, leaving a strong footprint. A likely area for price to return to.",
    },
    tech: {
      fr: "Dernière bougie de couleur opposée précédant une impulsion d'amplitude ≥ 1,2 × ATR(14) qui casse un extrême récent. La zone couvre le range de cette bougie.",
      en: "The last opposite-colored candle preceding an impulse of magnitude ≥ 1.2 × ATR(14) that breaks a recent extreme. The zone spans that candle's range.",
    },
    interpretation: {
      fr: "Zone probable de retour du prix : les acteurs institutionnels y ont laissé des ordres non exécutés qu'ils voudront finir de remplir.",
      en: "A probable price-return area: institutional players left unfilled orders there that they will want to complete.",
    },
    usage: {
      fr: "Zone d'entrée en tendance : achat sur ZII haussière touchée après pullback, stop juste sous la zone.",
      en: "Trend entry zone: buy when a bullish ZII is tagged on a pullback, stop just below the zone.",
    },
    oneLiner: {
      fr: "Empreinte institutionnelle récente — zone d'entrée en tendance",
      en: "Recent institutional footprint — trend entry zone",
    },
  },
  {
    key: "ZRI",
    acronym: "ZRI",
    family: "zones",
    name: { fr: "Zone de Rejet Importante", en: "Major Rejection Zone (French acronym kept as in the app)" },
    easy: {
      fr: "Une ancienne ZII qui a été cassée dans l'autre sens — elle change de polarité et devient un mur de rejet.",
      en: "A former ZII broken in the opposite direction — its polarity flips and it becomes a rejection wall.",
    },
    tech: {
      fr: "ZII violée par une clôture opposée à sa direction initiale. Polarité inversée : une ZII haussière violée devient résistance baissière.",
      en: "A ZII violated by a close against its original direction. Polarity inverts: a violated bullish ZII becomes bearish resistance.",
    },
    interpretation: {
      fr: "Signale un renversement de contrôle : les acteurs qui défendaient cette zone ont été balayés.",
      en: "Signals a shift in control: the players defending that zone have been swept out.",
    },
    usage: {
      fr: "Le retest d'une ZRI est une entrée à contre-courant du régime précédent, avec un ratio risque/récompense généralement élevé.",
      en: "A ZRI retest is a counter-trend entry against the prior regime, usually with a high risk/reward ratio.",
    },
    oneLiner: {
      fr: "Ancienne ZII cassée devenue mur de rejet — entrée contre-tendance",
      en: "Broken ZII turned rejection wall — counter-trend entry",
    },
  },
  {
    key: "DP",
    acronym: "DP",
    family: "zones",
    name: { fr: "Décision Point (vide de prix)", en: "Decision Point (price void)" },
    easy: {
      fr: "Un vide de prix : le marché a bougé si vite que 3 bougies consécutives ne se chevauchent pas. Le marché revient souvent combler ces vides.",
      en: "A price void: the market moved so fast that 3 consecutive candles don't overlap. Markets often come back to fill these gaps.",
    },
    tech: {
      fr: "Gap entre le high de la bougie N−1 et le low de la bougie N+1 (haussier) ou l'inverse (baissier). Uniquement retenu tant qu'il n'est pas comblé.",
      en: "Gap between candle N−1's high and candle N+1's low (bullish) or the reverse (bearish). Only kept while unfilled.",
    },
    interpretation: {
      fr: "Le marché revient souvent combler ces vides — une « zone magnétique » de correction.",
      en: "Price is often drawn back to fill the void — a 'magnetic' correction area.",
    },
    usage: {
      fr: "Cible de take-profit en tendance, ou zone d'entrée sur retest en contre-tendance courte.",
      en: "Take-profit target in trend, or a short counter-trend entry zone on retest.",
    },
    oneLiner: {
      fr: "Vide de prix aimant le marché — cible de TP ou entrée courte",
      en: "Price void that attracts the market — TP target or short entry",
    },
  },
  {
    key: "RL",
    acronym: "RL",
    family: "zones",
    name: { fr: "Recherche de Liquidité", en: "Liquidity Hunt (French acronym kept as in the app)" },
    easy: {
      fr: "Les anciens plus hauts / plus bas du graphique, où sont accumulés les stop-loss des autres traders — des « aimants » pour le prix. Séparés en sell-side (au-dessus) et buy-side (au-dessous).",
      en: "Prior swing highs/lows on the chart, where other traders' stop-losses cluster — price 'magnets'. Split into sell-side (above) and buy-side (below) liquidity.",
    },
    tech: {
      fr: "Swing highs / lows non balayés (pivot high/low avec left = right = 3). Classés sell-side au-dessus du prix et buy-side en dessous.",
      en: "Unswept swing highs/lows (pivot high/low with left = right = 3). Classified as sell-side above price and buy-side below.",
    },
    interpretation: {
      fr: "Le marché est structurellement attiré par ces niveaux pour déclencher les stops accumulés avant de repartir dans sa vraie direction.",
      en: "The market is structurally drawn to these levels to trigger the resting stops before resuming its true direction.",
    },
    usage: {
      fr: "Cibles naturelles de take-profit. Le balayage d'une RL peut aussi signaler un retournement (fausse cassure).",
      en: "Natural take-profit targets. A liquidity sweep can also signal a reversal (fake breakout).",
    },
    oneLiner: {
      fr: "Poches de stops (anciens hauts/bas) — cibles de TP et détecteur de fausses cassures",
      en: "Stop pools at prior highs/lows — TP targets and fake-breakout detector",
    },
  },

  // ============ STRUCTURE DE MARCHÉ ============
  {
    key: "CSD",
    acronym: "CSD",
    family: "structure",
    name: { fr: "Changement de Structure Dynamique", en: "Dynamic Structure Shift (French acronym kept as in the app)" },
    easy: {
      fr: "Le prix casse un ancien sommet (haussier) ou un ancien creux (baissier) — la confirmation qu'une tendance est en place.",
      en: "Price breaks a prior high (bullish) or a prior low (bearish) — confirmation that a trend is in place.",
    },
    tech: {
      fr: "Clôture d'une bougie au-dessus d'un swing high antérieur (bull CSD) ou en dessous d'un swing low antérieur (bear CSD).",
      en: "A candle close above a prior swing high (bull CSD) or below a prior swing low (bear CSD).",
    },
    interpretation: {
      fr: "Confirmation directionnelle : la structure de marché est haussière (ou baissière) jusqu'au prochain CSD opposé.",
      en: "Directional confirmation: market structure is bullish (or bearish) until the next opposite CSD.",
    },
    usage: {
      fr: "Filtre directionnel principal : n'acheter qu'après un bull CSD confirmé, ne vendre qu'après un bear CSD confirmé.",
      en: "Primary directional filter: only buy after a confirmed bull CSD, only sell after a confirmed bear CSD.",
    },
    oneLiner: {
      fr: "Cassure de structure confirmant la tendance — filtre directionnel principal",
      en: "Structure break confirming the trend — primary directional filter",
    },
  },
  {
    key: "BM",
    acronym: "BM",
    family: "structure",
    name: { fr: "Biais de Marché", en: "Market Bias (French acronym kept as in the app)" },
    easy: {
      fr: "Le premier signal — précoce, pas encore confirmé — d'un possible retournement : le marché commence à changer de direction.",
      en: "The first — early, not yet confirmed — signal of a possible reversal: the market starts to change direction.",
    },
    tech: {
      fr: "CSD dont la direction est opposée au CSD précédent. Indique un pivot potentiel.",
      en: "A CSD whose direction opposes the previous CSD. Flags a potential pivot.",
    },
    interpretation: {
      fr: "Signal PRÉCOCE — pas encore confirmé, mais annonce probablement un renversement.",
      en: "EARLY signal — not yet confirmed, but likely heralding a reversal.",
    },
    usage: {
      fr: "À combiner impérativement avec une ZII ou un DP — seul, il ne suffit pas. Augmente la confluence quand présent.",
      en: "Must be combined with a ZII or DP — never trade it alone. Adds confluence when present.",
    },
    oneLiner: {
      fr: "Signal précoce de retournement — jamais seul, toujours en confluence",
      en: "Early reversal signal — never alone, always as confluence",
    },
  },

  // ============ FENÊTRE TEMPORELLE & MOMENTUM ============
  {
    key: "FAF",
    acronym: "FAF",
    family: "timing",
    name: { fr: "Force d'Accélération Future", en: "Session Acceleration Window (French acronym kept as in the app)" },
    easy: {
      fr: "Les créneaux horaires où le marché bouge le plus : ouvertures de Londres et de New York. Le reste de la journée est souvent du bruit.",
      en: "The times of day when the market moves the most: the London and New York opens. The rest of the session is often noise.",
    },
    tech: {
      fr: "Bougies dans les créneaux 07h-10h UTC (session européenne) et 12h-15h UTC (session américaine) avec range > 1,1 × ATR.",
      en: "Candles inside the 07:00–10:00 UTC (European session) and 12:00–15:00 UTC (US session) windows with range > 1.1 × ATR.",
    },
    interpretation: {
      fr: "Ces fenêtres concentrent les mouvements directionnels ; hors FAF, le marché est majoritairement en range.",
      en: "These windows concentrate the directional moves; outside them, the market is mostly ranging.",
    },
    usage: {
      fr: "Filtre horaire : plus de poids aux signaux générés dans une FAF active, signaux hors FAF à ignorer en intraday.",
      en: "Time filter: weight signals inside an active FAF more heavily; ignore out-of-window signals intraday.",
    },
    oneLiner: {
      fr: "Fenêtres Londres / New York — filtre horaire des signaux",
      en: "London / New York windows — session filter for signals",
    },
  },
  {
    key: "IRL",
    acronym: "IRL",
    family: "timing",
    name: { fr: "Indice de Retournement Local", en: "Local Reversal Index (French acronym kept as in the app)" },
    easy: {
      fr: "Un changement brutal de momentum court terme : une série de bougies dans un sens qui bascule soudainement dans l'autre.",
      en: "An abrupt short-term momentum flip: a run of candles in one direction suddenly reversing into the other.",
    },
    tech: {
      fr: "Bascule du score (bougies vertes − bougies rouges) sur 6 bougies : de ≤ −2 à ≥ +2 (bull IRL) ou l'inverse (bear IRL).",
      en: "Flip of the (green candles − red candles) score over 6 candles: from ≤ −2 to ≥ +2 (bull IRL) or the reverse (bear IRL).",
    },
    interpretation: {
      fr: "Retournement de momentum court terme, souvent en début de nouvelle jambe de prix.",
      en: "Short-term momentum reversal, often at the start of a new price leg.",
    },
    usage: {
      fr: "Bonus de confluence pour un signal existant. Ne pas trader seul — bruit fréquent.",
      en: "Confluence bonus for an existing signal. Do not trade it alone — frequent noise.",
    },
    oneLiner: {
      fr: "Bascule brutale de momentum — bonus de confluence, jamais seul",
      en: "Sharp momentum flip — confluence bonus, never standalone",
    },
  },
  {
    key: "AAB",
    acronym: "AAB",
    family: "timing",
    name: { fr: "Action Algorithmique de Base", en: "Base Algorithmic Action (French acronym kept as in the app)" },
    easy: {
      fr: "On regarde la plus grande bougie récente comme une « bougie mère ». Si le prix est dans sa moitié haute, biais haussier ; dans sa moitié basse, biais baissier.",
      en: "The largest recent candle acts as a 'mother candle'. If price sits in its upper half, bullish bias; in its lower half, bearish bias.",
    },
    tech: {
      fr: "Sélection de la bougie au plus grand range (high − low) sur les 10 dernières bougies. Milieu = (high + low) / 2 ; le prix actuel est comparé à ce milieu.",
      en: "Select the candle with the largest range (high − low) over the last 10 candles. Midpoint = (high + low) / 2; current price is compared to that midpoint.",
    },
    interpretation: {
      fr: "Le prix pivote autour du milieu des grandes bougies avant de choisir une direction.",
      en: "Price pivots around the midpoint of large candles before committing to a direction.",
    },
    usage: {
      fr: "Filtre de confirmation directionnelle : achat privilégié quand le prix est dans la moitié haute d'une AAB, vente dans la moitié basse.",
      en: "Directional confirmation filter: favor longs when price is in the upper half of an AAB, shorts in the lower half.",
    },
    oneLiner: {
      fr: "Position du prix dans la « bougie mère » — filtre de confirmation directionnelle",
      en: "Price position inside the 'mother candle' — directional confirmation filter",
    },
  },

  // ============ RÉGIME DE MARCHÉ ============
  {
    key: "ADX",
    acronym: "ADX",
    family: "regime",
    name: { fr: "Average Directional Index (14)", en: "Average Directional Index (14)" },
    easy: {
      fr: "Mesure la FORCE d'une tendance, sans indiquer sa direction. Au-dessus de 25 : tendance forte. En dessous de 20 : pas de tendance (range).",
      en: "Measures trend STRENGTH, not direction. Above 25: strong trend. Below 20: no trend (range).",
    },
    tech: {
      fr: "Moyenne mobile de Wilder du DX = 100 × |+DI − −DI| / (+DI + −DI), période standard 14.",
      en: "Wilder moving average of DX = 100 × |+DI − −DI| / (+DI + −DI), standard period 14.",
    },
    interpretation: {
      fr: "Aveugle à la direction — donne uniquement l'intensité directionnelle du marché.",
      en: "Direction-blind — it only gives the market's directional intensity.",
    },
    usage: {
      fr: "Filtre de régime : les signaux de suivi de tendance ne sont autorisés que si l'ADX dépasse le seuil (22 dans l'algorithme).",
      en: "Regime filter: trend-following signals are only allowed when ADX exceeds the threshold (22 in the algorithm).",
    },
    oneLiner: {
      fr: "Force de la tendance (sans direction) — autorise ou bloque les signaux de tendance",
      en: "Trend strength (no direction) — gates trend-following signals on/off",
    },
  },
  {
    key: "HURST",
    acronym: "Hurst",
    family: "regime",
    name: { fr: "Exposant de Hurst", en: "Hurst Exponent" },
    easy: {
      fr: "Mesure si le marché est plutôt en tendance (> 0,55) ou en range / retour à la moyenne (< 0,45). 0,5 = comportement aléatoire.",
      en: "Measures whether the market is trending (> 0.55) or ranging / mean-reverting (< 0.45). 0.5 = random walk.",
    },
    tech: {
      fr: "Estimé par régression log-log de l'écart-type des différences de prix en fonction du lag (méthode rescaled range).",
      en: "Estimated via log-log regression of the standard deviation of price differences against the lag (rescaled range method).",
    },
    interpretation: {
      fr: "Signature mathématique du comportement du marché, indépendante de l'échelle de prix.",
      en: "A mathematical signature of market behavior, independent of price scale.",
    },
    usage: {
      fr: "Combiné à l'ADX pour classifier le régime de marché et adapter la stratégie (tendance vs range).",
      en: "Combined with ADX to classify the market regime and adapt the strategy (trend vs range).",
    },
    oneLiner: {
      fr: "Tendance ou retour à la moyenne ? — classification du régime avec l'ADX",
      en: "Trending or mean-reverting? — regime classification alongside ADX",
    },
  },

  // ============ PRÉVISION PROBABILISTE ============
  {
    key: "MC",
    acronym: "Monte-Carlo",
    family: "forecast",
    name: { fr: "Simulation Monte-Carlo", en: "Monte Carlo Simulation" },
    easy: {
      fr: "On rejoue un grand nombre de scénarios possibles du marché (à partir des rendements historiques) pour estimer une distribution de probabilité de hausse / baisse, plutôt qu'une prédiction unique.",
      en: "A large number of possible market paths are replayed (from historical returns) to estimate a probability distribution of up/down outcomes, instead of a single point prediction.",
    },
    tech: {
      fr: "Bootstrap des rendements log historiques (préserve les fat tails). N = 2 000 simulations sur un horizon H = 20 bougies.",
      en: "Bootstrap of historical log returns (preserves fat tails). N = 2,000 simulations over a horizon of H = 20 bars.",
    },
    interpretation: {
      fr: "Fournit une DISTRIBUTION honnête au lieu d'une prédiction ponctuelle ; les quantiles montrent l'incertitude réelle.",
      en: "Yields an honest DISTRIBUTION instead of a point forecast; the quantiles show the real uncertainty.",
    },
    usage: {
      fr: "P(hausse) > 60 % renforce la conviction sur un signal. Les quantiles q05 / q95 servent de stop-loss / take-profit naturels.",
      en: "P(up) > 60% strengthens conviction on a signal. The q05 / q95 quantiles serve as natural stop-loss / take-profit levels.",
    },
    oneLiner: {
      fr: "2 000 scénarios rejoués — probabilité de hausse et SL/TP naturels",
      en: "2,000 replayed paths — probability of upside and natural SL/TP levels",
    },
  },
];
